import {Semaphore} from "./Semaphore";
import {Lifetime} from "../Lifetime";

type ChannelMessage<T> =
    | { type: 'message', value: T }
    | { type: 'error', error: Error };

export class AsyncChannel<T> {
    private _semaphore = new Semaphore();
    private _buffer: ChannelMessage<T>[] = [];

    constructor(private readonly lifetime: Lifetime = new Lifetime()) {
        lifetime.onTermination(() => this._semaphore.release());
    }

    send(value: T) {
        if (this.lifetime.isTerminated) {
            throw new Error('Channel is closed');
        }
        this._buffer.push({type: 'message', value});
        this._semaphore.release();
    }

    close() {
        this.lifetime.terminate();
    }

    abort(error: Error) {
        this._buffer.push({type: 'error', error});
        this._semaphore.release();
    }

    async* incoming(): AsyncGenerator<T, void> {
        while (this.lifetime.isAlive) {
            if (this._buffer.length === 0) {
                await this._semaphore.acquire();
            }

            do {
                const next = this._buffer.shift();
                switch (next?.type) {
                    case undefined:
                        break;
                    case 'message':
                        yield next.value;
                        break;
                    case 'error':
                        this.lifetime.terminate();
                        throw next.error;
                }
            } while (this._buffer.length > 0)
        }
    }
}